import { PdfViewer } from "@/components/common/PdfViewer/PdfViewer"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useObjectUrl } from "@/hooks/useObjectUrl"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { downloadFile } from "@/utils/downloadUtils"
import { Download, FileText, Loader2 } from "lucide-react"
import {
  useNoteAttachmentBlob,
  type NoteAttachmentOut,
} from "../../../api/notesApi"

function Preview({ attachment }: { attachment: NoteAttachmentOut }) {
  const { translate } = useTranslation()
  const { data: blob, isError } = useNoteAttachmentBlob(attachment)
  const src = useObjectUrl(blob)
  const isImage = attachment.contentType.startsWith("image/")
  return (
    <>
      <DialogHeader className="space-y-0 flex-row items-center gap-2 border-b border-grey4 py-3 pr-12 pl-4 text-left">
        <FileText className="h-4 w-4 shrink-0 text-grey5" />
        <DialogTitle className="min-w-0 flex-1 truncate text-[13px] font-normal text-white">
          {attachment.fileName}
        </DialogTitle>
        <button
          type="button"
          disabled={!src}
          onClick={() => src && downloadFile(src, attachment.fileName)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-grey5 hover:bg-grey3 hover:text-white disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          {translate("MC_INQ_EMAIL_DOWNLOAD")}
        </button>
      </DialogHeader>
      <div className="min-h-0 flex-1 bg-grey1">
        {isError ? (
          <p
            role="alert"
            className="flex h-full items-center justify-center px-6 text-center text-[13px] text-grey5"
          >
            {translate("MC_NOTE_ERR_OPEN")}
          </p>
        ) : !src ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-grey5" />
          </div>
        ) : isImage ? (
          <div className="flex h-full items-center justify-center overflow-auto p-4">
            <img
              src={src}
              alt={attachment.fileName}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        ) : (
          <PdfViewer fileUrl={src} showControls={false} className="h-full" />
        )}
      </div>
    </>
  )
}

interface NoteAttachmentPreviewDialogProps {
  /** The file on show, or null while the dialog is closed. */
  attachment: NoteAttachmentOut | null
  onClose: () => void
}

/**
 * A note's file, shown in place: a PDF in the app's PDF viewer, an image as it is.
 * The stream route needs the bearer token, so the bytes come through the API client
 * and are shown from an object URL, never from the route's own URL.
 */
export function NoteAttachmentPreviewDialog({
  attachment,
  onClose,
}: NoteAttachmentPreviewDialogProps) {
  return (
    <Dialog
      open={attachment !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="flex h-[85vh] w-[90vw] max-w-[1000px] flex-col gap-0 overflow-hidden p-0"
      >
        {attachment && <Preview attachment={attachment} />}
      </DialogContent>
    </Dialog>
  )
}
